import * as THREE from 'three'
import { ProtoBase } from '../ProtoBase'
import { SITES, OLDEST_YEAR, toXZ, type KanjinSite } from '../kanjinData'

interface Pillar {
  site: KanjinSite
  group: THREE.Group
  layers: THREE.Mesh[]
  ring: THREE.Mesh
  orb: THREE.Sprite
  igniteAt: number
  grown: number
  height: number
}

/** ③ 光の御柱 v2 — 多層の光柱＋昇る光の粒＋頂の星。高さ＝古材の古さ（白鳳が最高） */
export class Proto3 extends ProtoBase {
  readonly title = '3. 光の御柱'
  readonly desc = '寄進地に神々しい光柱。高さ＝古材の古さ（白鳳664年が最高）。新しい時代から順に点灯'

  private pillars: Pillar[] = []
  private loopT = 0
  private motes!: THREE.Points
  private moteData: { pillar: number; speed: number }[] = []
  private motePos!: Float32Array

  protected start(): void {
    this.ctx.setWorld('map')
    this.ctx.setCaption('十二の寄進地に、御柱が立つ——柱の高さは、木の古さ')

    const glowTex = Proto3.makeGlowTexture()
    const sorted = [...SITES].sort((a, b) => b.year - a.year) // 新しい順に点灯
    sorted.forEach((site, i) => {
      const p = toXZ(site.lat, site.lon)
      const h = 5 + ((2026 - site.year) / (2026 - OLDEST_YEAR)) * 44
      const g = new THREE.Group()
      g.position.set(p.x, 0, p.z)

      // 三層の光柱（外=淡いオーラ / 中=金 / 芯=白）
      const layers: THREE.Mesh[] = []
      const specs: [number, number, number, number][] = [
        // [半径上, 半径下, 不透明度, 色]
        [2.0, 2.8, 0.13, 0xffdf9a],
        [0.85, 1.1, 0.35, 0xffc866],
        [0.2, 0.2, 0.95, 0xfff6dd],
      ]
      for (const [rt, rb, op, col] of specs) {
        const mesh = new THREE.Mesh(
          new THREE.CylinderGeometry(rt, rb, h, 12, 1, true),
          new THREE.MeshBasicMaterial({
            color: col, transparent: true, opacity: op,
            blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide,
          }),
        )
        mesh.scale.y = 0.001
        g.add(mesh)
        layers.push(mesh)
      }

      // 足元の光輪
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(1.6, 3.2, 32),
        new THREE.MeshBasicMaterial({
          color: 0xffd77a, transparent: true, opacity: 0,
          blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide,
        }),
      )
      ring.rotation.x = -Math.PI / 2
      ring.position.y = 0.08
      g.add(ring)

      // 頂の星
      const orb = new THREE.Sprite(new THREE.SpriteMaterial({
        map: glowTex, color: 0xfff2cc, transparent: true, opacity: 0,
        blending: THREE.AdditiveBlending, depthWrite: false,
      }))
      orb.position.y = h
      orb.scale.setScalar(0.001)
      g.add(orb)

      this.group.add(g)
      this.pillars.push({ site, group: g, layers, ring, orb, igniteAt: 1.2 + i * 1.05, grown: 0, height: h })
    })

    // 昇る光の粒（全柱共有のPoints）
    const perPillar = 9
    const n = this.pillars.length * perPillar
    this.motePos = new Float32Array(n * 3)
    for (let i = 0; i < n; i++) {
      const pi = Math.floor(i / perPillar)
      this.moteData.push({ pillar: pi, speed: 2 + Math.random() * 4 })
      const base = this.pillars[pi].group.position
      this.motePos[i * 3] = base.x + (Math.random() - 0.5) * 2.4
      this.motePos[i * 3 + 1] = Math.random() * this.pillars[pi].height
      this.motePos[i * 3 + 2] = base.z + (Math.random() - 0.5) * 2.4
    }
    const moteGeo = new THREE.BufferGeometry()
    moteGeo.setAttribute('position', new THREE.BufferAttribute(this.motePos, 3))
    this.motes = new THREE.Points(moteGeo, new THREE.PointsMaterial({
      color: 0xffe9b0, size: 0.9, transparent: true, opacity: 0.0,
      blending: THREE.AdditiveBlending, depthWrite: false, sizeAttenuation: true,
    }))
    this.motes.frustumCulled = false
    this.group.add(this.motes)
  }

  update(dt: number): void {
    this.elapsed += dt

    for (const p of this.pillars) {
      if (this.elapsed >= p.igniteAt && p.grown === 0) {
        p.grown = 0.0001
        this.ctx.sfx.play(p.site.year <= 700 ? 'drum' : 'bell', p.group.position)
        this.ctx.markers.highlight(p.site.no, 1)
        const y = p.site.year
        this.ctx.setCaption(`${p.site.era}${y > 900 ? `・${y}年` : y > 0 ? `（${y}年頃)` : ''}｜${p.site.building} — ${p.site.piece}`)
      }
      if (p.grown > 0 && p.grown < 1) {
        p.grown = Math.min(1, p.grown + dt / 0.9)
        const s = 1 - Math.pow(1 - p.grown, 3)
        for (const l of p.layers) {
          l.scale.y = s
          l.position.y = (p.height * s) / 2
        }
        ;(p.ring.material as THREE.MeshBasicMaterial).opacity = 0.55 * s
        p.orb.scale.setScalar(3.4 * s)
        ;(p.orb.material as THREE.SpriteMaterial).opacity = 0.9 * s
      }
      if (p.grown >= 1) {
        // 呼吸と揺らめき
        const w = Math.sin(this.elapsed * 2 + p.site.no)
        ;(p.layers[0].material as THREE.MeshBasicMaterial).opacity = 0.11 + 0.05 * w
        ;(p.layers[1].material as THREE.MeshBasicMaterial).opacity = 0.32 + 0.09 * w
        ;(p.ring.material as THREE.MeshBasicMaterial).opacity = 0.42 + 0.14 * Math.sin(this.elapsed * 3 + p.site.no)
        p.ring.scale.setScalar(1 + 0.06 * Math.sin(this.elapsed * 1.7 + p.site.no))
        p.orb.scale.setScalar(3.4 + 0.5 * Math.sin(this.elapsed * 2.4 + p.site.no))
      }
    }

    // 光の粒: 点灯済みの柱に沿って昇る
    const anyLit = this.pillars.some((p) => p.grown > 0)
    ;(this.motes.material as THREE.PointsMaterial).opacity = anyLit ? 0.85 : 0
    for (let i = 0; i < this.moteData.length; i++) {
      const d = this.moteData[i]
      const p = this.pillars[d.pillar]
      if (p.grown <= 0) continue
      let y = this.motePos[i * 3 + 1] + d.speed * dt
      if (y > p.height * p.grown) y = 0
      this.motePos[i * 3 + 1] = y
    }
    ;(this.motes.geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true

    const total = 1.2 + this.pillars.length * 1.05 + 1
    if (this.elapsed > total && this.loopT === 0) {
      this.ctx.setCaption('最も高い柱が、白鳳六六四年——太宰府・水城の杉。一畳に、千二百年が立っている')
      this.loopT = this.elapsed
    }
    if (this.loopT > 0 && this.elapsed - this.loopT > 7) {
      this.elapsed = 0
      this.loopT = 0
      for (const p of this.pillars) {
        p.grown = 0
        for (const l of p.layers) l.scale.y = 0.001
        ;(p.ring.material as THREE.MeshBasicMaterial).opacity = 0
        p.orb.scale.setScalar(0.001)
        ;(p.orb.material as THREE.SpriteMaterial).opacity = 0
      }
      this.ctx.markers.clearHighlights()
    }
  }

  private static makeGlowTexture(): THREE.CanvasTexture {
    const c = document.createElement('canvas')
    c.width = c.height = 128
    const ctx = c.getContext('2d')!
    const g = ctx.createRadialGradient(64, 64, 0, 64, 64, 64)
    g.addColorStop(0, 'rgba(255,246,220,1)')
    g.addColorStop(0.4, 'rgba(255,214,122,0.55)')
    g.addColorStop(1, 'rgba(255,200,100,0)')
    ctx.fillStyle = g
    ctx.fillRect(0, 0, 128, 128)
    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = THREE.SRGBColorSpace
    return tex
  }
}
